import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { catchError, timeout } from 'rxjs/operators';
import {
  IAccessChangesConfirmationResponse,
  IAccessChangesResponse,
  IUserAccessResponse,
} from 'src/app/core/models/access';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root',
})
export class AccessService {
  private readonly baseUrl = environment.apiBaseUrl + '/myhub-rbac/api/user';

  constructor(private http: HttpClient) {}

  // API Methods
  getUserAccess(idNumber: string): Observable<IUserAccessResponse> {
    return this.http.get<IUserAccessResponse>(`${this.baseUrl}/findByIdNumber?idNumber=${idNumber}`).pipe(
      catchError((error) => {
        console.error('=== Error fetching user access ===');
        console.error('ID Number:', idNumber);
        console.error('Request URL:', `${this.baseUrl}/findByIdNumber?idNumber=${idNumber}`);
        console.error('Error Status:', error.status);
        console.error('Error Status Text:', error.statusText);
        console.error('Error Message:', error.message);
        console.error('Error Headers:', error.headers);
        console.error('Error Body:', JSON.stringify(error.error, null, 2));
        console.error('Full Error Object:', JSON.stringify(error, null, 2));
        console.error('Error Stack:', error.stack);
        console.error('=== End Error Details ===');

        return throwError(() => error);
      }),
    );
  }

  /**
   * Fetch access change records for a guardian by ID number from the RBAC service.
   * Endpoint: /myhub-rbac/api/user/access/changes?idNumber={idNumber}
   */
  getAccessChanges(idNumber: string): Observable<IAccessChangesResponse | null> {
    if (!idNumber) {
      console.warn('getAccessChanges: No idNumber provided');
      return of(null);
    }

    const url = `${this.baseUrl}/access/changes?idNumber=${encodeURIComponent(idNumber)}`;

    return this.http.get<IAccessChangesResponse>(url).pipe(
      timeout(5000),
      catchError((error) => {
        console.error('=== Error fetching access changes ===');
        console.error('ID Number:', idNumber);
        console.error('Request URL:', url);
        console.error('Error Status:', error?.status);
        console.error('Error Status Text:', error?.statusText);
        console.error('Error Message:', error?.message);
        console.error('Error Headers:', error?.headers);
        console.error('Error Body:', JSON.stringify(error?.error, null, 2));
        console.error('Full Error Object:', JSON.stringify(error, null, 2));
        console.error('Error Stack:', error?.stack);
        console.error('=== End Error Details ===');

        return throwError(() => error);
      }),
    );
  }

  /**
   * Confirm that available grant/revoke changes have been acknowledged for a guardian.
   * POST /myhub-rbac/api/user/access/changes/confirmation
   */
  confirmAccessChanges(guardianIdNumber: string): Observable<IAccessChangesConfirmationResponse | null> {
    if (!guardianIdNumber) {
      console.warn('confirmAccessChanges: No guardianIdNumber provided');
      return of(null);
    }

    const url = `${this.baseUrl}/access/changes/confirmation`;
    const body = { guardianIdNumber };

    return this.http.post<IAccessChangesConfirmationResponse>(url, body).pipe(
      timeout(5000),
      catchError((error) => {
        console.error('=== Error confirming access changes ===');
        console.error('Guardian ID Number:', guardianIdNumber);
        console.error('Request URL:', url);
        console.error('Request Body:', JSON.stringify(body));
        console.error('Error Status:', error?.status);
        console.error('Error Status Text:', error?.statusText);
        console.error('Error Message:', error?.message);
        console.error('Error Body:', JSON.stringify(error?.error, null, 2));
        console.error('Full Error Object:', JSON.stringify(error, null, 2));
        console.error('Error Stack:', error?.stack);
        console.error('=== End Error Details ===');

        return throwError(() => error);
      }),
    );
  }
}
